import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { apiBlob, apiDescarrega, apiMultipart, apiRequest } from './client';
import type {
  ActivacioResumDto,
  BlocDestinatari,
  Canal,
  Cobertura,
  EstatActivacio,
  EstatDestinatari,
  Pagina,
  TipusActivacio,
  TipusArea,
} from './types';

export type MotiuTancament = 'RESOLTA' | 'MITIGADA' | 'CANCELLADA' | 'FINALITZADA';
export type TipusMencio = 'USUARI' | 'SERVEI' | 'DEPARTAMENT';
export type TipusMissatge =
  'TEXT' | 'RISC' | 'AFECTACIO' | 'NOVETAT' | 'RECURSOS' | 'SISTEMA' | 'FINALITZACIO';
export type TipusAccio =
  | 'CREADA'
  | 'ENVIADA'
  | 'CONFIRMADA'
  | 'ACCEPTADA'
  | 'REENVIADA'
  | 'ESCALADA'
  | 'EXPERT_ACTIVAT'
  | 'NOTA'
  | 'ITCAU_VINCULAT'
  | 'TANCADA'
  | 'DOCUMENT_CONSULTAT';
export type EstatDocument = 'ESBORRANY' | 'PUBLICAT' | 'RETIRAT' | 'BLOQUEJAT';

export const ESTATS_OBERTS: EstatActivacio[] = ['OBERTA', 'EN_CURS'];
export const MOTIUS_TANCAMENT: MotiuTancament[] = [
  'RESOLTA',
  'MITIGADA',
  'CANCELLADA',
  'FINALITZADA',
];
export const CANALS: Canal[] = ['PUSH', 'PUSH_CRITICA', 'EMAIL', 'TRUCADA'];
export const BLOCS: BlocDestinatari[] = [
  'TECNIC_GUARDIA',
  'USUARIS_INFORMAR',
  'TECNIC_EXPERT',
  'ADDICIONAL',
];

export function esOberta(estat: EstatActivacio): boolean {
  return ESTATS_OBERTS.includes(estat);
}

export interface ActivacioFiltres {
  desde?: string;
  fins?: string;
  serveiId?: number;
  areaId?: number;
  departamentId?: number;
  zonaId?: number;
  tipus?: TipusActivacio;
  critica?: boolean;
  estat?: EstatActivacio;
  personaId?: number;
  text?: string;
  areaTipus?: TipusArea;
  page?: number;
  size?: number;
}

export interface ItcauDto {
  ref?: string | null;
  url?: string | null;
}

export interface TancamentDto {
  motiu?: MotiuTancament | null;
  comentari?: string | null;
  at?: string | null;
  /** Identificador de l'usuari que ha tancat l'activació. */
  per?: number | null;
  missatge?: string | null;
  mesuresPendents?: string | null;
}

export interface ActivacioDto extends ActivacioResumDto {
  tipusAvisId?: number | null;
  serveiIds: number[];
  areaId?: number | null;
  departamentId?: number | null;
  departament?: string | null;
  zonaId?: number | null;
  zonaText?: string | null;
  activadorId?: number | null;
  primeraConfirmacioAt?: string | null;
  descripcio?: string | null;
  provesRealitzades?: string | null;
  iniciIncident?: string | null;
  contacteId?: number | null;
  contacteText?: string | null;
  risc?: string | null;
  perills?: string | null;
  mesures?: string | null;
  plantillaId?: number | null;
  missatge?: string | null;
  canals: Canal[];
  circuitEscalatId?: number | null;
  itcau?: ItcauDto | null;
  tancament?: TancamentDto | null;
  version?: number;
  /** L'usuari actual ha silenciat les notificacions dels missatges del xat d'aquesta activació. */
  silenciada?: boolean;
}

export interface CanalEstatDto {
  canal: Canal;
  estat: string;
  error?: string | null;
}

export interface DestinatariDto {
  id: number;
  usuariId?: number | null;
  nom: string;
  bloc: BlocDestinatari;
  nivell: number;
  estat: EstatDestinatari;
  acceptat?: boolean;
  enviatAt?: string | null;
  lliuratAt?: string | null;
  llegitAt?: string | null;
  confirmatAt?: string | null;
  canals?: CanalEstatDto[];
}

export interface MencioDto {
  tipus: TipusMencio;
  id: number;
  nom: string;
}

export interface MissatgeDto {
  id?: number;
  autorId?: number | null;
  autor?: string | null;
  tipus: TipusMissatge;
  text: string;
  mencions?: MencioDto[];
  zonaId?: number | null;
  zonaText?: string | null;
  prioritatAlta?: boolean;
  difondre?: boolean;
  nomesConfirmats?: boolean;
  canals?: Canal[];
  notificats?: number;
  at?: string;
}

export interface AccioDto {
  id: number;
  tipus: TipusAccio;
  usuariId?: number | null;
  usuari?: string | null;
  detall?: Record<string, unknown> | null;
  at: string;
}

/** `detall` d'una acció NOTA (missatge del xat). És una imatge si té `adjuntId`. */
export interface DetallNotaDto {
  text?: string | null;
  adjuntId?: number | string | null;
  adjuntNom?: string | null;
  adjuntTipus?: string | null;
  adjuntMida?: number | null;
}

export function detallNota(ac: AccioDto): DetallNotaDto {
  return (ac.detall ?? {}) as DetallNotaDto;
}

export function esImatge(ac: AccioDto): boolean {
  return ac.tipus === 'NOTA' && detallNota(ac).adjuntId != null;
}

/** Límits de les imatges del xat (el servidor també accepta HEIC, que els navegadors no mostren). */
export const TIPUS_IMATGE = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MIDA_MAX_IMATGE = 10 * 1024 * 1024;
export const MIDA_MAX_TEXT = 4000;

/** Comprovació al client abans de pujar una imatge del xat. */
export function validaImatge(f: { type: string; size: number }): 'tipus' | 'mida' | null {
  if (!(TIPUS_IMATGE as readonly string[]).includes(f.type)) return 'tipus';
  if (f.size > MIDA_MAX_IMATGE) return 'mida';
  return null;
}

export interface DocumentDto {
  id: number;
  codi?: string | null;
  servei?: string | null;
  titol: string;
  estat: EstatDocument;
  responsable?: string | null;
  versioVigent?: number | null;
  updatedAt?: string | null;
  descarregable: boolean;
}

export interface NivellEscalatDto {
  ordre: number;
  tipusDesti: string;
  destiId?: number | null;
  esperaSeg: number;
  canals: Canal[];
}

export interface CircuitEscalatDto {
  id: number;
  nom: string;
  perDefecte: boolean;
  nivells: NivellEscalatDto[];
}

export interface ServeiCercaDto {
  serveiId?: number | null;
  serveiCodi?: string | null;
  serveiNom?: string | null;
  areaId?: number | null;
  areaNom?: string | null;
  tipus?: TipusArea | null;
  departamentId: number;
  departamentNom: string;
  plantillaId?: number | null;
}

export interface TipusAvisDto {
  id: number;
  codi: string;
  nom: string;
  tipusBase: TipusActivacio;
  criticaPermesa: boolean;
  canalsObligatoris: Canal[];
  tempsConfirmacioSeg: number;
  color?: string | null;
}

export interface ZonaDto {
  id: number;
  codi: string;
  nom: string;
  tipus: string;
}

export interface UsuariDto {
  id: number;
  nom: string;
  cognoms?: string | null;
  email?: string | null;
}

export interface DestinatarisAltaDto {
  tecnicGuardia: boolean;
  usuarisInformar: boolean;
  expert: boolean;
  addicionals: { usuariId?: number; grupId?: number }[];
}

export interface ActivacioAltaDto {
  serveiIds: number[];
  departamentId?: number | null;
  tipusAvisId?: number | null;
  tipus?: TipusActivacio;
  critica: boolean;
  titol: string;
  descripcio?: string;
  provesRealitzades?: string;
  iniciIncident?: string;
  zonaId?: number | null;
  zonaText?: string;
  contacteId?: number | null;
  contacteText?: string;
  risc?: string;
  perills?: string;
  mesures?: string;
  plantillaId?: number | null;
  variables?: Record<string, string>;
  missatge?: string;
  canals?: Canal[];
  destinataris?: DestinatarisAltaDto;
}

export interface PrevisualitzacioDto {
  titol: string;
  missatge: string;
  destinataris: Partial<Record<BlocDestinatari, DestinatariDto[]>>;
  missatgePerCanal: Partial<Record<Canal, string>>;
}

export interface TancarDto {
  motiu: MotiuTancament;
  comentari?: string;
  missatgeFinalitzacio?: string;
  mesuresPendents?: string;
  plantillaId?: number | null;
  canals?: Canal[];
  notificar: boolean;
}

export interface TecnicGuardiaDto {
  titularId?: number | null;
  titularNom?: string | null;
  /** El servidor serialitza el model TecnicGuardia sencer (GuardiesRest): inclou els telèfons. */
  titularTelefon?: string | null;
  substitutNom?: string | null;
  substitutTelefon?: string | null;
  fins?: string | null;
}

export interface GuardiaAreaDto {
  id: number;
  nom: string;
  tipus: TipusArea;
  tecnic?: TecnicGuardiaDto | null;
  cobertura?: Cobertura | null;
}

export interface GuardiaAraDto {
  instant?: string;
  departaments: {
    id: number;
    nom: string;
    tecnic?: TecnicGuardiaDto | null;
    arees: GuardiaAreaDto[];
  }[];
}

export interface RecompteDto {
  clau: string;
  nom: string;
  total: number;
}

export interface IndicadorsDto {
  activacions: number;
  perServei: RecompteDto[];
  perZona: RecompteDto[];
  perTipus: RecompteDto[];
  tempsMitjaPrimeraConfirmacioSeg?: number | null;
  tempsMitjaEscalatSeg?: number | null;
  tempsMitjaResolucioSeg?: number | null;
  percentatgeConfirmacio?: number | null;
  noLliurats: number;
  ambEscalat: number;
  complimentTemps?: number | null;
  serieTemporal: { data: string; total: number }[];
  recurrents?: RecurrentDto[];
}

export interface RecurrentDto {
  serveiId?: number | null;
  servei?: string | null;
  zonaId?: number | null;
  zona?: string | null;
  total: number;
}

export function query(params: object): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const claus = {
  activacions: ['activacions'] as const,
  llista: (f: ActivacioFiltres): QueryKey => ['activacions', f],
  activacio: (id: number) => ['activacio', id] as const,
  part: (id: number, part: string) => ['activacio', id, part] as const,
};

export function useActivacions(filtres: ActivacioFiltres, enabled = true) {
  return useQuery({
    queryKey: claus.llista(filtres),
    queryFn: () => apiRequest<Pagina<ActivacioResumDto>>(`/activacions${query(filtres)}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export const obteActivacio = (id: number) => apiRequest<ActivacioDto>(`/activacions/${id}`);

export function useActivacio(id: number) {
  return useQuery({
    queryKey: claus.activacio(id),
    queryFn: () => obteActivacio(id),
    enabled: Number.isFinite(id),
  });
}

/**
 * Imatge d'un missatge del xat (amb el token). Clau fora de `['activacio', id]` perquè les
 * invalidacions del detall no la tornin a descarregar: el contingut d'una acció no canvia.
 */
export function useImatgeAccio(activacioId: number, accioId: number) {
  return useQuery({
    queryKey: ['imatge-accio', activacioId, accioId] as const,
    queryFn: () => apiBlob(`/activacions/${activacioId}/accions/${accioId}/imatge`),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
  });
}

/** Silencia o reactiva les notificacions del xat d'una activació (actualització optimista). */
export function useSilenci(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (silenciada: boolean) => accionsApi.silenci(id, silenciada),
    onMutate: async (silenciada: boolean) => {
      await qc.cancelQueries({ queryKey: claus.activacio(id), exact: true });
      const abans = qc.getQueryData<ActivacioDto>(claus.activacio(id));
      if (abans) qc.setQueryData<ActivacioDto>(claus.activacio(id), { ...abans, silenciada });
      return { abans };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.abans) qc.setQueryData(claus.activacio(id), ctx.abans);
    },
    onSuccess: (r) => {
      const actual = qc.getQueryData<ActivacioDto>(claus.activacio(id));
      if (actual) qc.setQueryData<ActivacioDto>(claus.activacio(id), { ...actual, ...r });
    },
  });
}

function useParteActivacio<T>(id: number, part: string, enabled = true) {
  return useQuery({
    queryKey: claus.part(id, part),
    queryFn: () => apiRequest<T>(`/activacions/${id}/${part}`),
    enabled: enabled && Number.isFinite(id),
  });
}

export const useMissatges = (id: number) => useParteActivacio<MissatgeDto[]>(id, 'missatges');
export const useAccions = (id: number) => useParteActivacio<AccioDto[]>(id, 'accions');
export const useDestinataris = (id: number) =>
  useParteActivacio<DestinatariDto[]>(id, 'destinataris');
export const useDocuments = (id: number) => useParteActivacio<DocumentDto[]>(id, 'documents');

export function useCircuit(serveiId: number | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['circuit', serveiId],
    queryFn: () => apiRequest<CircuitEscalatDto>(`/serveis/${serveiId}/circuit-escalat`),
    enabled: enabled && serveiId != null,
    retry: false,
  });
}

export function useGuardiesAra(enabled = true) {
  return useQuery({
    queryKey: ['guardies', 'ara'],
    queryFn: () => apiRequest<GuardiaAraDto>('/guardies/ara'),
    enabled,
  });
}

export function useIndicadors(desde: string, enabled: boolean) {
  return useQuery({
    queryKey: ['indicadors', desde],
    queryFn: () => apiRequest<IndicadorsDto>(`/informes/indicadors${query({ desde })}`),
    enabled,
  });
}

export function useCercaServeis(text: string) {
  return useQuery({
    queryKey: ['serveis', 'cerca', text],
    queryFn: () => apiRequest<ServeiCercaDto[]>(`/serveis/cerca${query({ text })}`),
    placeholderData: keepPreviousData,
  });
}

export function useTipusAvis() {
  return useQuery({
    queryKey: ['tipus-avis'],
    queryFn: () => apiRequest<TipusAvisDto[]>('/tipus-avis'),
    staleTime: 300_000,
  });
}

export function useZones() {
  return useQuery({
    queryKey: ['zones'],
    queryFn: () => apiRequest<ZonaDto[]>('/zones'),
    staleTime: 300_000,
  });
}

export interface UsuariCercaDto extends UsuariDto {
  departament?: string | null;
}

/** Cerca d'usuaris per a mencions i destinataris addicionals (no requereix permisos d'administració). */
export function useCercaUsuaris(text: string) {
  return useQuery({
    queryKey: ['usuaris', 'cerca', text],
    queryFn: () =>
      apiRequest<UsuariCercaDto[]>(`/usuaris/cerca${query({ text: text.trim(), limit: 20 })}`),
    enabled: text.trim().length >= 2,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export interface TaulerDiaDto {
  data: string;
  it: number;
  crisi: number;
}

export interface TaulerDto {
  obertes: number;
  obertesIt: number;
  obertesCrisi: number;
  areesCrisiActives?: number | null;
  tempsMitjaRespostaSeg?: number | null;
  percentatgeConfirmacio?: number | null;
  confirmats: number;
  destinataris: number;
  areesAmbGuardia: number;
  areesTotals: number;
  tancadesMes: number;
  perDia: TaulerDiaDto[];
}

/** KPI del tauler calculats al servidor (RF-026). */
export function useTauler(enabled = true) {
  return useQuery({
    queryKey: ['activacions', 'tauler'],
    queryFn: () => apiRequest<TaulerDto>('/informes/tauler'),
    enabled,
    staleTime: 30_000,
  });
}

export interface CandidatEscalatDto {
  usuariId: number;
  nom?: string | null;
  expert: boolean;
  especialitat?: string | null;
}

export function useCandidatsEscalat(id: number, text: string) {
  return useQuery({
    queryKey: [...claus.part(id, 'candidats-escalat'), text],
    queryFn: () =>
      apiRequest<CandidatEscalatDto[]>(
        `/activacions/${id}/candidats-escalat${query({ text: text.trim() || undefined })}`,
      ),
    placeholderData: keepPreviousData,
    enabled: Number.isFinite(id),
  });
}

export function useAccioActivacio<V, R = unknown>(id: number, fer: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fer,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claus.activacio(id) });
      void qc.invalidateQueries({ queryKey: claus.activacions });
    },
  });
}

export const accionsApi = {
  nota: (id: number, text: string) =>
    apiRequest<AccioDto>(`/activacions/${id}/accions`, { method: 'POST', json: { text } }),
  /** Missatge amb imatge (acció NOTA amb adjunt); el text és el peu de la imatge. */
  imatge: (id: number, fitxer: File, text?: string) => {
    const form = new FormData();
    form.append('fitxer', fitxer, fitxer.name);
    if (text?.trim()) form.append('text', text.trim());
    return apiMultipart<AccioDto>(`/activacions/${id}/accions/imatge`, form);
  },
  silenci: (id: number, silenciada: boolean) =>
    apiRequest<{ silenciada: boolean }>(`/activacions/${id}/silenci`, {
      method: 'PUT',
      json: { silenciada },
    }),
  missatge: (id: number, m: MissatgeDto) =>
    apiRequest<MissatgeDto>(`/activacions/${id}/missatges`, { method: 'POST', json: m }),
  expert: (id: number, expertId?: number) =>
    apiRequest<DestinatariDto>(`/activacions/${id}/expert`, {
      method: 'POST',
      json: expertId ? { expertId } : {},
    }),
  escalar: (id: number, motiu: string, usuariId?: number) =>
    apiRequest<ActivacioDto>(`/activacions/${id}/escalar`, {
      method: 'POST',
      json: usuariId ? { motiu, usuariId } : { motiu },
    }),
  reenviar: (id: number, destinatariIds: number[], canals: Canal[]) =>
    apiRequest<{ enviaments: number }>(`/activacions/${id}/reenviar`, {
      method: 'POST',
      json: { destinatariIds, canals },
    }),
  confirmar: (id: number) =>
    apiRequest<DestinatariDto>(`/activacions/${id}/confirmar`, {
      method: 'POST',
      json: { canal: 'WEB' },
    }),
  acceptar: (id: number) =>
    apiRequest<DestinatariDto>(`/activacions/${id}/acceptar`, { method: 'POST' }),
  afegeixDestinataris: (id: number, usuariIds: number[], grupIds: number[] = []) =>
    apiRequest<DestinatariDto[]>(`/activacions/${id}/destinataris`, {
      method: 'POST',
      json: { usuariIds, grupIds },
    }),
  tancar: (id: number, cos: TancarDto) =>
    apiRequest<ActivacioDto>(`/activacions/${id}/tancar`, { method: 'POST', json: cos }),
  itcau: (id: number, cos: ItcauDto) =>
    apiRequest<ActivacioDto>(`/activacions/${id}/itcau`, { method: 'PUT', json: cos }),
  previsualitzar: (cos: ActivacioAltaDto) =>
    apiRequest<PrevisualitzacioDto>('/activacions/previsualitzacio', {
      method: 'POST',
      json: cos,
    }),
  informe: (id: number, codi: string, format: 'pdf' | 'csv' | 'xlsx') =>
    apiDescarrega(`/activacions/${id}/informe?format=${format}`, `${codi}.${format}`),
  document: (id: number, docId: number, nom: string) =>
    apiDescarrega(`/activacions/${id}/documents/${docId}/descarrega`, nom),
  crear: (cos: ActivacioAltaDto, clau: string, forcar = false) =>
    apiRequest<ActivacioDto>(`/activacions${forcar ? '?forcar=true' : ''}`, {
      method: 'POST',
      json: cos,
      headers: { 'Idempotency-Key': clau },
    }),
};
